import config from '@config';
import { getErrorMessage } from '@utils';
import _ from 'lodash';
import { flow } from 'mobx-state-tree';

export const mapPaymentMethod = (paymentMethod) => {
    if (!paymentMethod) return null;

    return paymentMethod.type === 'card'
        ? _.pick(paymentMethod, ['type', 'brand', 'last4', 'exp_month', 'exp_year'])
        : _.pick(paymentMethod, ['type', 'bank_name', 'status', 'last4', 'verification_link']);
};

export const parseContentDispositionFilename = (contentDisposition) => {
    if (!contentDisposition) {
        throw new Error('Missing content-disposition header');
    }

    const parts = contentDisposition.split('filename=');
    if (parts.length < 2) {
        throw new Error('Invalid content-disposition header format');
    }

    return parts[1].replace(/['"]/g, '').trim();
};

export function parseMstError(error) {
    if (!error || !error.message) return null;

    const errorMessage = error.message;
    const errorInfo = {
        type: 'MST_TYPE_ERROR',
        originalMessage: errorMessage,
        parsedErrors: []
    };

    const patterns = [
        {
            name: 'modern_type_assignment',
            regex: /at path "([^"]+)" value `(.*?)` is not assignable to type: `([^`]+)`/gs,
            parse: (matches) =>
                matches.map(([, path, value, expected]) => ({
                    errorType: 'Type Assignment Error',
                    field: path.replace(/^\//, ''),
                    receivedValue: value,
                    expectedType: expected,
                    description: `Field "${path.replace(
                        /^\//,
                        ''
                    )}" received value "${value}" but expected type: ${expected}`
                }))
        },
        {
            name: 'detailed_type_error',
            regex: /at path "([^"]+)" value `(.*?)` is not assignable to type: `([^`]+)` \(([^)]+)\)/gs,
            parse: (matches) =>
                matches.map(([, path, value, expected, reason]) => ({
                    errorType: 'Type Assignment Error',
                    field: path.replace(/^\//, ''),
                    receivedValue: value,
                    expectedType: expected,
                    description: `Field "${path.replace(
                        /^\//,
                        ''
                    )}" received value "${value}" but expected type: ${expected}. Reason: ${reason}`
                }))
        },
        {
            name: 'legacy_type_assignment',
            regex: /at path "([^"]+)" value `(.*?)` is not assignable to type: \(([^)]+)\)/gs,
            parse: (matches) =>
                matches.map(([, path, value, expected]) => ({
                    errorType: 'Type Assignment Error',
                    field: path.replace(/^\//, ''),
                    receivedValue: value,
                    expectedType: expected,
                    description: `Field "${path.replace(
                        /^\//,
                        ''
                    )}" received value "${value}" but expected type: ${expected}`
                }))
        },
        {
            name: 'required_field',
            regex: /at path "([^"]+)" value `(.*?)` is not assignable.*required/gs,
            parse: (matches) =>
                matches.map(([, path, value]) => ({
                    errorType: 'Required Field Error',
                    field: path.replace(/^\//, ''),
                    receivedValue: value,
                    expectedType: 'required value',
                    description: `Required field "${path.replace(
                        /^\//,
                        ''
                    )}" is missing or has invalid value "${value}"`
                }))
        },
        {
            name: 'enum_error',
            regex: /at path "([^"]+)" value `(.*?)` is not assignable.*enumeration\(([^)]+)\)/gs,
            parse: (matches) =>
                matches.map(([, path, value, enumValues]) => ({
                    errorType: 'Enum Value Error',
                    field: path.replace(/^\//, ''),
                    receivedValue: value,
                    expectedType: `one of: ${enumValues}`,
                    description: `Field "${path.replace(
                        /^\//,
                        ''
                    )}" received "${value}" but must be one of: ${enumValues}`
                }))
        },
        {
            name: 'array_error',
            regex: /at path "([^"]+)" value `(.*?)` is not assignable.*array/gs,
            parse: (matches) =>
                matches.map(([, path, value]) => ({
                    errorType: 'Array Type Error',
                    field: path.replace(/^\//, ''),
                    receivedValue: value,
                    expectedType: 'array',
                    description: `Field "${path.replace(/^\//, '')}" received "${value}" but expected an array`
                }))
        }
    ];

    patterns.forEach((pattern) => {
        const matches = [...errorMessage.matchAll(pattern.regex)];
        if (matches.length > 0) {
            errorInfo.parsedErrors.push(...pattern.parse(matches));
        }
    });

    const uniqueErrors = [];
    const seen = new Set();

    errorInfo.parsedErrors.forEach((error) => {
        const key = `${error.field}|${error.receivedValue}|${error.expectedType}`;
        if (!seen.has(key)) {
            seen.add(key);
            uniqueErrors.push(error);
        }
    });

    errorInfo.parsedErrors = uniqueErrors;

    if (errorInfo.parsedErrors.length === 0) {
        errorInfo.parsedErrors.push({
            errorType: 'Unknown MST Error',
            field: 'unknown',
            receivedValue: 'unknown',
            expectedType: 'unknown',
            description: errorMessage
        });
    }

    return errorInfo;
}

function isMobXErrorByStack(error) {
    if (!error || !error.stack) return false;

    const stack = error.stack;

    const mobxStackPatterns = [
        /mobx-state-tree/i,
        /mobx\.esm\.js/i,
        /executeAction/i,
        /runMiddleWares/i,
        /runWithActionContext/i
    ];

    return mobxStackPatterns.some((pattern) => pattern.test(stack));
}

export const createStoreAction =
    (self) =>
    (fn, options = {}) =>
        flow(function* (...args) {
            const rootStore = self.root;
            if (rootStore && typeof rootStore.clearAllMessages === 'function') {
                rootStore.clearAllMessages();
            }

            try {
                const response = yield* fn.apply(self, args);

                if (options?.getSuccessMessage && typeof options?.getSuccessMessage === 'function') {
                    const message = options?.getSuccessMessage(...args);
                    if (message) rootStore.setSuccess(message);
                }

                return response;
            } catch (err) {
                if (config.stage !== 'production' && isMobXErrorByStack(err)) {
                    const parsedError = parseMstError(err);
                    console.error('MST Type Error:', parsedError); // eslint-disable-line

                    rootStore.setError({
                        title: 'Type Error',
                        description: parsedError.parsedErrors[0]?.errorType || parsedError.originalMessage
                    });

                    return { error: err };
                }

                if (options?.onError && typeof options?.onError === 'function') {
                    return options.onError(err, ...args);
                }

                rootStore.setError(getErrorMessage(err));

                return { error: err };
            } finally {
                if (options?.onFinally && typeof options?.onFinally === 'function') options.onFinally(...args);
            }
        });
